import React, { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { CheckIcon, XIcon } from "./Icons";
import { CodeBlock, InlineMarkdown, Markdown } from "./Markdown";

/** One multiple-choice question. `answer` is the chosen option once answered, otherwise undefined. */
function QuestionCard({ question, eyebrow, answer, onAnswer, children }) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState(answer ?? null);
  const name = useId();
  const answered = answer !== undefined;
  const isCorrect = answered && answer === question.answer;
  // Options of "what does this print?" questions are literal program output, not prose.
  const literal = question.verify === "output";

  return (
    <article className={`question ${answered ? (isCorrect ? "is-correct" : "is-incorrect") : ""}`}>
      {eyebrow && <p className="eyebrow">{eyebrow}</p>}
      <InlineMarkdown as="h2" className="question__prompt" source={question.prompt} />
      {question.code && <CodeBlock code={question.code} />}

      <fieldset className="options" disabled={answered}>
        <legend className="visually-hidden">{question.prompt}</legend>
        {question.options.map((option, optionIndex) => {
          const text = String(option);
          let state = "";
          if (answered && optionIndex === question.answer) state = "is-answer";
          else if (answered && optionIndex === answer) state = "is-wrong";
          return (
            <label key={optionIndex} className={`option ${state} ${selected === optionIndex ? "is-selected" : ""}`}>
              <input
                type="radio"
                name={name}
                value={optionIndex}
                checked={selected === optionIndex}
                onChange={() => setSelected(optionIndex)}
              />
              {literal ? <code className="option__literal">{text}</code> : <InlineMarkdown source={text} />}
              {state === "is-answer" && <CheckIcon size={15} strokeWidth={3} className="option__mark" />}
              {state === "is-wrong" && <XIcon size={15} strokeWidth={3} className="option__mark" />}
            </label>
          );
        })}
      </fieldset>

      {!answered ? (
        <button
          type="button"
          className="button button--primary button--small"
          disabled={selected === null}
          onClick={() => onAnswer(selected)}
        >
          {t("quiz.check")}
        </button>
      ) : (
        <div className="question__feedback" role="status">
          <strong>{isCorrect ? t("quiz.correct") : t("quiz.incorrect")}</strong>
          <Markdown source={question.explanation} />
          {children}
        </div>
      )}
    </article>
  );
}

export default QuestionCard;
